import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Plus, Trash2, Star, Edit3, X, ChevronDown, ChevronUp,
  DollarSign, Percent, Clock, Home, BarChart3, ArrowRight,
  Loader2, CheckCircle, AlertTriangle, FileText
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { fmtUSD } from "../utils/currency";
const fmt$ = (v) => fmtUSD(v);
const LOAN_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};
const ACCENT = { fix_flip: "amber", bridge: "blue", dscr: "emerald", cash_out: "purple", construction: "orange", ground_up: "teal", multifamily: "indigo", commercial: "pink" };

const EMPTY_FORM = {
  scenario_name: "", loan_type: "fix_flip", property_address: "", property_type: "single_family", state: "",
  purchase_price: "", arv: "", rehab_budget: "", loan_amount: "", interest_rate: "", term_months: "12",
  closing_costs: "", exit_strategy: "sell_retail", notes: "",
};

const SCENARIO_PREFILL_KEY = "imaani_scenario_prefill";

import { usePageNavigate } from "../hooks/usePageNavigate";

export default function DealScenariosTab() {
  const { navigateTo } = usePageNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchScenarios = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/scenarios`, { headers });
      setScenarios(r.data.scenarios || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  const handleSave = async () => {
    if (!form.scenario_name.trim() || !form.loan_type) {
      toast.error("Scenario name and loan type are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      purchase_price: parseInt(form.purchase_price) || 0,
      arv: parseInt(form.arv) || 0,
      rehab_budget: parseInt(form.rehab_budget) || 0,
      loan_amount: parseInt(form.loan_amount) || 0,
      interest_rate: parseFloat(form.interest_rate) || 0,
      term_months: parseInt(form.term_months) || 12,
      closing_costs: parseInt(form.closing_costs) || 0,
    };
    try {
      if (editId) {
        await axios.put(`${API}/scenarios/${editId}`, payload, { headers });
        toast.success("Scenario updated");
      } else {
        await axios.post(`${API}/scenarios`, payload, { headers });
        toast.success("Scenario created");
      }
      setShowForm(false);
      setEditId(null);
      setForm({ ...EMPTY_FORM });
      fetchScenarios();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await axios.delete(`${API}/scenarios/${id}`, { headers });
      toast.success("Scenario deleted");
      setCompareIds(prev => prev.filter(x => x !== id));
      fetchScenarios();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handlePrefer = async (id) => {
    try {
      await axios.post(`${API}/scenarios/${id}/prefer`, {}, { headers });
      toast.success("Marked as preferred");
      fetchScenarios();
    } catch (e) { toast.error("Failed"); }
  };

  const startEdit = (s) => {
    setForm({
      scenario_name: s.scenario_name, loan_type: s.loan_type,
      property_address: s.property_address || "", property_type: s.property_type || "",
      state: s.state || "", purchase_price: String(s.purchase_price || ""),
      arv: String(s.arv || ""), rehab_budget: String(s.rehab_budget || ""),
      loan_amount: String(s.loan_amount || ""), interest_rate: String(s.interest_rate || ""),
      term_months: String(s.term_months || "12"), closing_costs: String(s.closing_costs || ""),
      exit_strategy: s.exit_strategy || "", notes: s.notes || "",
    });
    setEditId(s.scenario_id);
    setShowForm(true);
  };

  const toggleCompare = (id) => {
    setCompareIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const handleCreateDeal = (scenario) => {
    const EXIT_MAP = { sell_retail: "flip", sell_wholesale: "sell", refinance: "refinance", hold_rental: "rent" };
    const PROP_MAP = { single_family: "sfr", multi_family: "multi", condo: "condo", townhouse: "condo", mixed_use: "mixed_use", commercial: "commercial" };
    const prefill = {
      loanType: scenario.loan_type || "",
      propertyAddress: scenario.property_address || "",
      propertyType: PROP_MAP[scenario.property_type] || "",
      state: scenario.state || "",
      purchasePrice: String(scenario.purchase_price || ""),
      arv: String(scenario.arv || ""),
      rehabBudget: String(scenario.rehab_budget || ""),
      requestedLoanAmount: String(scenario.loan_amount || ""),
      exitStrategy: EXIT_MAP[scenario.exit_strategy] || "",
      notes: scenario.notes || "",
    };
    try { localStorage.setItem(SCENARIO_PREFILL_KEY, JSON.stringify(prefill)); } catch {}
    if (navigateTo) navigateTo("apply");
  };

  const comparedScenarios = scenarios.filter(s => compareIds.includes(s.scenario_id));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-6" data-testid="deal-scenarios-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[#0A0A0A]">Financing Scenarios</h2>
          <p className="text-xs text-[#888] mt-0.5">Compare different financing strategies before submitting a capital request.</p>
        </div>
        <div className="flex items-center gap-2">
          {compareIds.length >= 2 && (
            <button onClick={() => setShowCompare(true)}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1.5"
              data-testid="compare-btn">
              <BarChart3 className="h-3.5 w-3.5" /> Compare ({compareIds.length})
            </button>
          )}
          <button onClick={() => { setForm({ ...EMPTY_FORM }); setEditId(null); setShowForm(true); }}
            className="px-3 py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors flex items-center gap-1.5"
            data-testid="new-scenario-btn">
            <Plus className="h-3.5 w-3.5" /> New Scenario
          </button>
        </div>
      </div>

      {/* Scenario Cards */}
      {scenarios.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-[#e8e0d0] rounded-2xl" data-testid="empty-scenarios">
          <BarChart3 className="h-10 w-10 mx-auto mb-3 text-[#ccc]" />
          <h3 className="text-sm font-bold text-[#555]">No scenarios yet</h3>
          <p className="text-xs text-[#888] mt-1 max-w-xs mx-auto">Create financing scenarios to compare different loan types, rates, and terms side by side.</p>
          <button onClick={() => { setForm({ ...EMPTY_FORM }); setEditId(null); setShowForm(true); }}
            className="mt-4 px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Create Your First Scenario
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {scenarios.map((s) => {
            const accent = ACCENT[s.loan_type] || "gray";
            const isCompared = compareIds.includes(s.scenario_id);
            return (
              <motion.div key={s.scenario_id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl border bg-white overflow-hidden transition-all ${
                  isCompared ? "border-blue-400 ring-2 ring-blue-100" : "border-[#e8e0d0] hover:border-[#ccc]"
                }`} data-testid={`scenario-card-${s.scenario_id}`}>
                {/* Card Header */}
                <div className={`px-4 py-3 border-b border-[#f0ebe0] bg-${accent}-50/30`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      {s.is_preferred && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                      <h3 className="text-sm font-bold text-[#222] truncate">{s.scenario_name}</h3>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold bg-${accent}-100 text-${accent}-700`}>
                      {LOAN_LABELS[s.loan_type] || s.loan_type}
                    </span>
                  </div>
                  {s.property_address && <p className="text-[10px] text-[#888] mt-1 truncate">{s.property_address}</p>}
                </div>

                {/* Key Metrics */}
                <div className="px-4 py-3 grid grid-cols-2 gap-2">
                  <MetricCell label="Loan Amount" value={fmt$(s.loan_amount)} />
                  <MetricCell label="Rate" value={s.interest_rate ? `${s.interest_rate}%` : "--"} />
                  <MetricCell label="LTC" value={`${s.ltc || 0}%`} warn={s.ltc > 85} />
                  <MetricCell label="LTV" value={`${s.ltv || 0}%`} warn={s.ltv > 75} />
                  <MetricCell label="Monthly" value={fmt$(s.monthly_payment)} />
                  <MetricCell label="Term" value={s.term_months ? `${s.term_months} mo` : "--"} />
                </div>

                {/* Actions */}
                <div className="px-3 py-2.5 border-t border-[#f0ebe0] flex items-center justify-between">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={isCompared} onChange={() => toggleCompare(s.scenario_id)}
                      className="rounded border-[#ccc] text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                      data-testid={`compare-check-${s.scenario_id}`} />
                    <span className="text-[10px] text-[#888]">Compare</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleCreateDeal(s)} title="Start Application"
                      className="px-2 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-[10px] font-semibold transition-colors flex items-center gap-1"
                      data-testid={`create-deal-btn-${s.scenario_id}`}>
                      <FileText className="h-3 w-3" /> Apply
                    </button>
                    {!s.is_preferred && (
                      <button onClick={() => handlePrefer(s.scenario_id)} title="Set preferred"
                        className="p-1.5 rounded-lg hover:bg-amber-50 text-[#bbb] hover:text-amber-600 transition-colors"
                        data-testid={`prefer-btn-${s.scenario_id}`}>
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => startEdit(s)} title="Edit"
                      className="p-1.5 rounded-lg hover:bg-blue-50 text-[#bbb] hover:text-blue-600 transition-colors"
                      data-testid={`edit-btn-${s.scenario_id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(s.scenario_id)} title="Delete"
                      className="p-1.5 rounded-lg hover:bg-red-50 text-[#bbb] hover:text-red-600 transition-colors"
                      data-testid={`delete-btn-${s.scenario_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto mx-4 sm:mx-0"
              data-testid="scenario-form-modal">
              <div className="sticky top-0 bg-white border-b border-[#e8e0d0] px-4 sm:px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
                <h3 className="text-sm font-bold text-[#222]">{editId ? "Edit Scenario" : "New Financing Scenario"}</h3>
                <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-[#f5f5f5]"><X className="h-4 w-4 text-[#888]" /></button>
              </div>
              <div className="px-4 sm:px-5 py-5 space-y-4">
                <FormField label="Scenario Name" required>
                  <input value={form.scenario_name} onChange={e => setForm({...form, scenario_name: e.target.value})}
                    placeholder="e.g., Fix & Flip Aggressive" className="form-input" data-testid="form-scenario-name" />
                </FormField>
                <FormField label="Loan Program" required>
                  <select value={form.loan_type} onChange={e => setForm({...form, loan_type: e.target.value})} className="form-input" data-testid="form-loan-type">
                    {Object.entries(LOAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </FormField>
                <FormField label="Property Address">
                  <input value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})}
                    placeholder="123 Main St, City State" className="form-input" data-testid="form-address" />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="State">
                    <input value={form.state} onChange={e => setForm({...form, state: e.target.value})} placeholder="TX" className="form-input" />
                  </FormField>
                  <FormField label="Property Type">
                    <select value={form.property_type} onChange={e => setForm({...form, property_type: e.target.value})} className="form-input">
                      <option value="single_family">Single Family</option>
                      <option value="multi_family">Multi-Family</option>
                      <option value="condo">Condo</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="mixed_use">Mixed Use</option>
                      <option value="commercial">Commercial</option>
                    </select>
                  </FormField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Purchase Price ($)">
                    <input type="number" value={form.purchase_price} onChange={e => setForm({...form, purchase_price: e.target.value})} placeholder="285000" className="form-input" data-testid="form-purchase-price" />
                  </FormField>
                  <FormField label="ARV ($)">
                    <input type="number" value={form.arv} onChange={e => setForm({...form, arv: e.target.value})} placeholder="425000" className="form-input" data-testid="form-arv" />
                  </FormField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Rehab Budget ($)">
                    <input type="number" value={form.rehab_budget} onChange={e => setForm({...form, rehab_budget: e.target.value})} placeholder="75000" className="form-input" />
                  </FormField>
                  <FormField label="Loan Amount ($)">
                    <input type="number" value={form.loan_amount} onChange={e => setForm({...form, loan_amount: e.target.value})} placeholder="306000" className="form-input" data-testid="form-loan-amount" />
                  </FormField>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FormField label="Rate (%)">
                    <input type="number" step="0.1" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} placeholder="11.5" className="form-input" data-testid="form-rate" />
                  </FormField>
                  <FormField label="Term (months)">
                    <input type="number" value={form.term_months} onChange={e => setForm({...form, term_months: e.target.value})} placeholder="12" className="form-input" />
                  </FormField>
                  <FormField label="Closing Costs ($)">
                    <input type="number" value={form.closing_costs} onChange={e => setForm({...form, closing_costs: e.target.value})} placeholder="8500" className="form-input" />
                  </FormField>
                </div>
                <FormField label="Exit Strategy">
                  <select value={form.exit_strategy} onChange={e => setForm({...form, exit_strategy: e.target.value})} className="form-input">
                    <option value="sell_retail">Sell (Retail)</option>
                    <option value="sell_wholesale">Sell (Wholesale)</option>
                    <option value="refinance">Refinance</option>
                    <option value="hold_rental">Hold as Rental</option>
                    <option value="other">Other</option>
                  </select>
                </FormField>
                <FormField label="Notes">
                  <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                    rows={2} placeholder="Any additional details..." className="form-input resize-none" />
                </FormField>
              </div>
              <div className="sticky bottom-0 bg-white border-t border-[#e8e0d0] px-4 sm:px-5 py-3 flex justify-end gap-2 rounded-b-2xl">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-xs font-medium text-[#666] hover:bg-[#f5f5f5]">Cancel</button>
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="scenario-save-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                  {editId ? "Update Scenario" : "Create Scenario"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comparison Modal */}
      <AnimatePresence>
        {showCompare && comparedScenarios.length >= 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => e.target === e.currentTarget && setShowCompare(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto mx-4 sm:mx-0"
              data-testid="comparison-modal">
              <div className="sticky top-0 bg-white border-b border-[#e8e0d0] px-4 sm:px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
                <h3 className="text-sm font-bold text-[#222]">Scenario Comparison</h3>
                <button onClick={() => setShowCompare(false)} className="p-1 rounded-lg hover:bg-[#f5f5f5]"><X className="h-4 w-4 text-[#888]" /></button>
              </div>
              <div className="px-4 sm:px-5 py-4 overflow-x-auto">
                <table className="w-full text-xs" data-testid="comparison-table">
                  <thead>
                    <tr className="border-b border-[#e8e0d0]">
                      <th className="text-left py-2 pr-4 text-[9px] text-[#999] uppercase font-bold w-32">Metric</th>
                      {comparedScenarios.map(s => (
                        <th key={s.scenario_id} className="text-right py-2 px-2 min-w-[100px]">
                          <div className="text-xs font-bold text-[#333]">{s.scenario_name}</div>
                          <div className="text-[9px] text-[#999]">{LOAN_LABELS[s.loan_type]}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "purchase_price", label: "Purchase Price", fmt: fmt$ },
                      { key: "arv", label: "ARV", fmt: fmt$ },
                      { key: "rehab_budget", label: "Rehab Budget", fmt: fmt$ },
                      { key: "total_cost", label: "Total Cost", fmt: fmt$ },
                      { key: "loan_amount", label: "Loan Amount", fmt: fmt$ },
                      { key: "interest_rate", label: "Interest Rate", fmt: v => v ? `${v}%` : "--" },
                      { key: "term_months", label: "Term", fmt: v => v ? `${v} months` : "--" },
                      { key: "monthly_payment", label: "Monthly Payment", fmt: fmt$ },
                      { key: "closing_costs", label: "Closing Costs", fmt: fmt$ },
                      { key: "ltc", label: "LTC Ratio", fmt: v => `${v || 0}%`, warnAbove: 85 },
                      { key: "ltv", label: "LTV Ratio", fmt: v => `${v || 0}%`, warnAbove: 75 },
                    ].map((metric, i) => {
                      const values = comparedScenarios.map(s => s[metric.key] || 0);
                      const best = metric.key === "interest_rate" || metric.key === "monthly_payment" || metric.key === "closing_costs"
                        ? Math.min(...values.filter(v => v > 0))
                        : metric.key === "ltc" || metric.key === "ltv"
                        ? Math.min(...values.filter(v => v > 0))
                        : Math.max(...values);
                      return (
                        <tr key={metric.key} className={i % 2 === 0 ? "bg-[#faf7f2]/50" : ""}>
                          <td className="py-2.5 pr-4 text-[10px] text-[#888] font-semibold">{metric.label}</td>
                          {comparedScenarios.map(s => {
                            const val = s[metric.key] || 0;
                            const isBest = val === best && val > 0 && values.filter(v => v === best).length < values.length;
                            const isWarn = metric.warnAbove && val > metric.warnAbove;
                            return (
                              <td key={s.scenario_id} className={`text-right py-2.5 px-2 text-xs font-semibold ${
                                isBest ? "text-emerald-700" : isWarn ? "text-amber-600" : "text-[#333]"
                              }`}>
                                {metric.fmt(val)}
                                {isBest && <span className="ml-1 text-[8px] text-emerald-500">BEST</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MetricCell({ label, value, warn }) {
  return (
    <div className="py-1">
      <div className="text-[9px] text-[#999] uppercase">{label}</div>
      <div className={`text-xs font-bold mt-0.5 ${warn ? "text-amber-600" : "text-[#222]"}`}>
        {value}
        {warn && <AlertTriangle className="inline h-2.5 w-2.5 ml-0.5 text-amber-500" />}
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-[#888] uppercase tracking-wider mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: `${children.props.className || ""} w-full px-3 py-2 rounded-lg border border-[#e0dace] text-xs text-[#333] bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-400`.trim()
      })}
    </div>
  );
}
